import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { format, parse, isValid } from "date-fns";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { typeIcon } from "@/components/shared/CustomFieldForm";
import { Check, ChevronDown, Pencil } from "lucide-react";
import { useCanMutate } from "@/lib/rbac";
import EditableLocationField from "@/components/shared/EditableLocationField";
import { useCurrency } from "@/contexts/CurrencyContext";
import { formatCurrencyValue, formatNumberValue } from "@/lib/utils/custom-column";
import { trafficLightOptionsWithLabels } from "@/lib/utils/custom-column";
import { formatPhone } from "@/components/shared/CustomColumnCell";
import { columnLabel, type GridEntity } from "@/lib/grid/column-label";
import { toast } from "sonner";
import { displayDate } from "@/lib/i18n/display";

export interface MorePanelField {
  key: string;
  label: string;
  type: string; // custom field type or standard type string
  value?: string | number | boolean | string[] | null;
  options?: string[];
  isCustom?: boolean;
  readOnly?: boolean;
  /** Overrides the type-based icon so standard fields keep the same icon
   *  they use in the primary field list when hidden/shown. */
  icon?: React.ComponentType<{ className?: string }>;
}

interface Props {
  open: boolean;
  onClose?: () => void;
  fields: MorePanelField[];
  onChange: (key: string, value: string | boolean | string[]) => void;
  /**
   * Which grid's field catalog the standard rows belong to. Supplied by the
   * record detail sheets; omit it and every label renders verbatim, which is
   * what a panel of purely custom fields wants.
   */
  entity?: GridEntity;
}

/** Display format for a date cell. Hoisted so it is a token, not UI copy. */
const DATE_DISPLAY_FORMAT = "MMM d, yyyy";

const compactControlClass =
  "inline-flex h-5 max-w-full items-center justify-start gap-2 rounded -mx-1 px-1 py-0 text-xs font-normal leading-5 text-foreground transition hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

const compactMenuItemClass =
  "flex h-6 w-full items-center gap-2 rounded px-2 text-left text-xs leading-none transition hover:bg-accent hover:text-accent-foreground";

/**
 * Inline expandable block listing every remaining field for a record.
 * Renders in-flow (not as a sliding panel) so it uses the same left column
 * as the primary field list, matching its row styling for visual consistency.
 */
export default function MoreFieldsPanel({ open, fields, onChange, entity }: Props) {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    <div className="mt-2">
      {fields.length > 0 && (
        <FieldGroup title="" fields={fields} onChange={onChange} entity={entity} />
      )}
      {fields.length === 0 && (
        <p className="text-xs text-muted-foreground italic">{t("grid.morePanel.noAdditionalFields")}</p>
      )}
    </div>
  );
}

function FieldGroup({
  title, fields, onChange, entity,
}: { title: string; fields: MorePanelField[]; onChange: Props["onChange"]; entity?: GridEntity }) {
  return (
    <div className="space-y-2">
      {title && (
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground/70 font-medium">
          {title}
        </div>
      )}
      <div className="space-y-0.5">
        {fields.map(f => (
          <MoreFieldRow key={f.key} field={f} onChange={onChange} entity={entity} />
        ))}
      </div>
    </div>
  );
}

export function MoreFieldRow({
  field, onChange, entity,
}: { field: MorePanelField; onChange: Props["onChange"]; entity?: GridEntity }) {
  const { t } = useTranslation();
  const Icon = field.icon ?? typeIcon(field.type);
  // Custom fields are workspace data — never translated. Standard rows resolve
  // the same `fields.<entity>.<key>` catalog the grid header uses.
  const label = field.isCustom ? field.label : columnLabel(entity, field, t);
  return (
    <div className="grid grid-cols-[130px_1fr] gap-3 items-start py-1 [&_.text-sm]:text-xs">
      <div className="flex items-center gap-2 text-xs text-muted-foreground pt-0.5">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <div className="relative min-w-0 flex items-center text-xs text-foreground">
        <FieldEditor field={field} onChange={onChange} />
      </div>
    </div>
  );
}

function FieldEditor({
  field, onChange,
}: { field: MorePanelField; onChange: Props["onChange"] }) {
  const { t } = useTranslation();
  const commit = (v: string | boolean | string[]) => onChange(field.key, v);
  const { symbol: currencySymbol } = useCurrency();

  if (field.readOnly) {
    return <ReadOnlyValue value={field.value} />;
  }

  switch (field.type) {
    case "date":
      return <DateEditor value={String(field.value ?? "")} onChange={v => commit(v)} />;
    case "single-select":
      return <SelectEditor value={String(field.value ?? "")} options={field.options ?? []} onChange={v => commit(v)} />;
    case "multi-select": {
      const parsed = Array.isArray(field.value)
        ? field.value
        : (field.value ? String(field.value).split("||").map(s => s.trim()).filter(Boolean) : []);
      return <MultiSelectEditor value={parsed} options={field.options ?? []} onChange={v => commit(v.join("||"))} />;
    }
    case "checkbox":
      return <CheckboxEditor checked={!!field.value && field.value !== "false"} onChange={v => commit(v)} />;
    case "traffic-light":
      return <TrafficLightEditor value={String(field.value ?? "")} labels={field.options} onChange={v => commit(v)} />;
    case "number":
      return (
        <TextEditor
          value={String(field.value ?? "")}
          onCommit={commit}
          type="text"
          inputMode="decimal"
          formatDisplay={formatNumberValue}
          validate={(v) => !v || /^-?\d+(\.\d+)?$/.test(v.trim())}
          invalidMessage={t("grid.validation.number")}
        />
      );
    case "currency":
      return (
        <TextEditor
          value={String(field.value ?? "")}
          onCommit={commit}
          type="text"
          inputMode="decimal"
          formatDisplay={(v) => formatCurrencyValue(v, currencySymbol)}
          validate={(v) => !v || /^-?\d+(\.\d+)?$/.test(v.trim())}
          invalidMessage={t("grid.validation.number")}
        />
      );
    case "url":
      return <TextEditor value={String(field.value ?? "")} onCommit={commit} isLink validate={(v) => !v || /^(https?:\/\/)?[\w.-]+\.\w{2,}(\/.*)?$/i.test(v)} invalidMessage={t("grid.validation.url")} />;
    case "location":
      return (
        <EditableLocationField
          compact
          hideIcon
          value={String(field.value ?? "")}
          onChange={v => commit(v)}
        />
      );
    case "email":
      return (
        <TextEditor
          value={String(field.value ?? "")}
          onCommit={commit}
          type="email"
          validate={(v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)}
          invalidMessage={t("grid.validation.email")}
        />
      );
    case "phone":
      return (
        <TextEditor
          value={String(field.value ?? "")}
          onCommit={commit}
          type="tel"
          inputMode="tel"
          formatDisplay={formatPhone}
          validate={(v) => !v || /^[+\d\s().-]{0,20}$/.test(v)}
          invalidMessage={t("grid.validation.phone")}
        />
      );
    case "ai":
    case "formula":
      return <ReadOnlyValue value={field.value} muted />;
    default:
      return <TextEditor value={String(field.value ?? "")} onCommit={commit} />;
  }
}

function ReadOnlyValue({ value, muted }: { value: MorePanelField["value"]; muted?: boolean }) {
  if (value === null || value === undefined || value === "" || value === "-") {
    return <span className="text-muted-foreground/60">—</span>;
  }
  return (
    <span className={cn("break-words", muted ? "text-muted-foreground text-xs" : "text-foreground")}>
      {Array.isArray(value) ? value.join(", ") : String(value)}
    </span>
  );
}

function TextEditor({
  value, onCommit, type = "text", prefix, placeholder, isLink, inputMode, formatDisplay, validate, invalidMessage,
}: { value: string; onCommit: (v: string) => void; type?: string; prefix?: string; placeholder?: string; isLink?: boolean; inputMode?: "text" | "decimal" | "email" | "tel" | "url" | "numeric"; formatDisplay?: (v: string) => string; validate?: (v: string) => boolean; invalidMessage?: string }) {
  const { t } = useTranslation();
  const canMutate = useCanMutate();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => {
    if (editing) setTimeout(() => inputRef.current?.focus(), 0);
  }, [editing]);

  const isValid = validate ? validate(draft) : true;
  const commit = () => {
    if (!isValid) {
      if (invalidMessage) toast.error(invalidMessage);
      setDraft(value);
      setEditing(false);
      return;
    }
    setEditing(false);
    if (draft !== value) onCommit(draft);
  };

  if (editing) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 group rounded -mx-1 px-1 py-0.5 -my-0.5 transition min-w-0",
          // URL fields break out to a wider overlay so the full URL is visible
          // while editing without covering the label column.
          isLink ? "absolute left-0 top-0 z-30 bg-popover border border-border shadow-md min-w-[360px]" : "w-full",
        )}
      >
        {prefix && <span className="text-muted-foreground text-xs">{prefix}</span>}
        <input
          ref={inputRef}
          value={draft}
          placeholder={placeholder}
          type={type}
          inputMode={inputMode}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === "Enter") {
              if (!isValid) {
                if (invalidMessage) toast.error(invalidMessage);
                e.preventDefault();
                return;
              }
              commit();
            }
            if (e.key === "Escape") { setDraft(value); setEditing(false); }
          }}
          className={cn(
            "block w-full min-w-0 bg-transparent outline-none text-xs p-0 m-0 border-0 focus:ring-0",
            !isValid && "text-destructive",
          )}
        />
      </div>
    );
  }

  const shortened = isLink && value
    ? (value.includes("linkedin.com")
        ? "/" + value.replace(/^https?:\/\//, "").replace(/^(www\.)?linkedin\.com\/(in|company)\//, "").replace(/\/$/, "")
        : value.replace(/^https?:\/\//, ""))
    : value;
  const formatted = formatDisplay && value ? formatDisplay(value) : shortened;
  const display = formatted ? (prefix ? `${prefix}${formatted}` : formatted) : "";
  if (isLink && value) {
    const href = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    return (
      <div className="flex items-center gap-2 group rounded -mx-1 px-1 py-0.5 transition">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="truncate min-w-0 text-xs text-accent hover:underline"
          title={value}
        >
          {display}
        </a>
        {canMutate && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            aria-label={t("common.edit")}
            className="shrink-0"
          >
            <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition" />
          </button>
        )}
      </div>
    );
  }
  return (
    <div
      className={cn(
        "flex items-center gap-2 group rounded -mx-1 px-1 py-0.5 transition",
        canMutate && "cursor-pointer hover:bg-muted/30",
      )}
      onClick={() => canMutate && setEditing(true)}
    >
      <span className={cn("truncate min-w-0 text-xs", !value && "text-muted-foreground/60", isLink && value && "text-accent")}>
        {display || placeholder || "—"}
      </span>
      {canMutate && (
        <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition shrink-0" />
      )}
    </div>
  );
}

function DateEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const parsed = useMemo(() => {
    if (!value) return undefined;
    // Parse YYYY-MM-DD as local date to avoid UTC shift showing prior day.
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (iso) {
      const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
      if (isValid(d)) return d;
    }
    const alt = parse(value, "MM/dd/yyyy", new Date());
    if (isValid(alt)) return alt;
    const d = new Date(value);
    return isValid(d) ? d : undefined;
  }, [value]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={compactControlClass}>
          <span className={cn("truncate", !parsed && "text-muted-foreground")}>{parsed ? displayDate(parsed) : t("grid.cell.pickDate")}</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[60]" align="start">
        <Calendar
          mode="single"
          selected={parsed}
          onSelect={d => onChange(d ? format(d, "yyyy-MM-dd") : "")}
          initialFocus
          className={cn("p-3 pointer-events-auto")}
        />
      </PopoverContent>
    </Popover>
  );
}

function SelectEditor({
  value, options, onChange,
}: { value: string; options: string[]; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected = value || "";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={cn(compactControlClass, "w-full")}>
          <span className={cn("min-w-0 flex-1 truncate text-left", !selected && "text-muted-foreground")}>{selected || t("grid.cell.selectEllipsis")}</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-1 z-[60] w-[180px]" align="start">
        <div className="max-h-56 overflow-y-auto">
          {options.map(o => {
            const active = selected === o;
            return (
              <button
                key={o}
                type="button"
                onClick={() => { onChange(o); setOpen(false); }}
                className={cn(compactMenuItemClass, active && "bg-accent text-accent-foreground")}
              >
                <Check className={cn("h-3 w-3 shrink-0", !active && "opacity-0")} />
                <span className="truncate">{o}</span>
              </button>
            );
          })}
          {options.length === 0 && (
            <p className="text-xs text-muted-foreground px-2 py-2">{t("grid.cell.noOptions")}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CheckboxEditor({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" onClick={() => onChange(!checked)} className={compactControlClass}>
      <span
        className={cn(
          "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border border-border",
          checked && "border-primary bg-primary text-primary-foreground",
        )}
      >
        {checked && <Check className="h-3 w-3" />}
      </span>
      <span className="truncate">{checked ? t("grid.cell.yes") : t("grid.cell.no")}</span>
    </button>
  );
}

function TrafficLightEditor({ value, labels, onChange }: { value: string; labels?: string[]; onChange: (v: string) => void }) {
  const opts = trafficLightOptionsWithLabels(labels);
  const current = opts.find(o => o.value === value) ?? opts[opts.length - 1];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={compactControlClass}>
          <span className={cn("h-2.5 w-2.5 rounded-full", current.color)} />
          <span className={cn("truncate", !value && "text-muted-foreground")}>{current.label}</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-1 z-[60] w-[140px]" align="start">
        {opts.map(o => (
          <button
            key={o.label}
            type="button"
            onClick={() => onChange(o.value)}
            className={compactMenuItemClass}
          >
            <span className={cn("h-2.5 w-2.5 rounded-full", o.color)} />
            <span>{o.label}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function MultiSelectEditor({
  value, options, onChange,
}: { value: string[]; options: string[]; onChange: (v: string[]) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const toggle = (opt: string) => {
    const set = new Set(value);
    if (set.has(opt)) set.delete(opt); else set.add(opt);
    onChange(Array.from(set));
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={cn(compactControlClass, "w-full")}>
          <span className={cn("min-w-0 flex-1 truncate text-left", value.length === 0 && "text-muted-foreground")}>{value.length > 0 ? value.join(", ") : t("grid.cell.selectEllipsis")}</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-1 z-[60] w-[220px]" align="start">
        <div className="max-h-56 overflow-y-auto">
          {options.map(o => {
            const active = value.includes(o);
            return (
              <button
                key={o}
                type="button"
                onClick={() => toggle(o)}
                className={cn(
                  compactMenuItemClass,
                  active && "text-foreground"
                )}
              >
                <span className={cn(
                  "h-3.5 w-3.5 rounded-sm border border-border flex items-center justify-center shrink-0",
                  active && "border-primary bg-primary text-primary-foreground"
                )}>
                  {active && <Check className="h-3 w-3" />}
                </span>
                <span className="truncate">{o}</span>
              </button>
            );
          })}
          {options.length === 0 && (
            <p className="text-xs text-muted-foreground px-2 py-2">{t("grid.cell.noOptions")}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}